import {
  type AllocationDraftCell,
  buildCustomAllocationSubAccounts,
  type SubAccountCustomAllocationInput,
} from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import type {
  AccountAllocationGridAccount,
  AccountAllocationGridHolding,
  AccountAllocationGridReport,
} from './types';

const CASH_FINANCIAL_PRODUCT_ID = 'CASH';

type MatrixRow = AccountAllocationGridHolding & {
  financialProduct: AccountAllocationGridHolding['financialProduct'] & {
    id: string;
  };
};

const isCashHolding = (holding: AccountAllocationGridHolding) =>
  holding.financialProduct.id === CASH_FINANCIAL_PRODUCT_ID ||
  holding.isCashEquivalent === true ||
  holding.financialProduct.isCash === true;

export const matrixRows = (report: AccountAllocationGridReport): MatrixRow[] =>
  report.holdings.filter(
    (holding): holding is MatrixRow =>
      Boolean(holding.financialProduct.id) && !isCashHolding(holding),
  );

type CellFractionRow = Pick<
  AccountAllocationGridHolding,
  'expectedPercentage'
> & {
  financialProduct: { id: string };
};

export const cellFraction = (
  account: AccountAllocationGridAccount,
  row: CellFractionRow,
): number | undefined => {
  const held = account.holdings.find(
    (holding) => holding.financialProduct.id === row.financialProduct.id,
  );
  if (!held) return undefined;
  return held.customExpectedPercentage ?? row.expectedPercentage;
};

export const placeholderPayload = (
  report: AccountAllocationGridReport,
): SubAccountCustomAllocationInput[] => {
  const rows = matrixRows(report);
  const cells: AllocationDraftCell[] = [];

  for (const account of report.subAccounts) {
    for (const row of rows) {
      const fraction = cellFraction(account, row);
      if (fraction === undefined) continue;
      cells.push({
        subAccountId: account.subAccount.id,
        financialProductId: row.financialProduct.id,
        customExpectedPercentage: fraction,
      });
    }
  }

  return buildCustomAllocationSubAccounts(cells);
};
